import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { getUserColor } from '@/core/lib/colors';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { Badge } from '@/shared/components/ui/badge';
import { Separator } from '@/shared/components/ui/separator';
import type { MemberSummaryDto } from '../../types';

function priorityColor(priority: number): string {
  if (priority >= 4) return 'text-destructive';
  if (priority === 3) return 'text-orange-500';
  if (priority === 2) return 'text-blue-500';
  return 'text-muted-foreground';
}

function priorityKey(priority: number): string {
  if (priority >= 4) return 'urgent';
  if (priority === 3) return 'high';
  if (priority === 2) return 'medium';
  return 'low';
}

export interface MemberSummaryPopoverProps {
  displayName: string | null;
  avatarUrl: string | null;
  memberId: string;
  summary: MemberSummaryDto | undefined;
  isLoading: boolean;
}

export function MemberSummaryPopover({
  displayName,
  avatarUrl,
  memberId,
  summary,
  isLoading,
}: MemberSummaryPopoverProps) {
  const { t } = useTranslation('organizationUnits');
  const color = getUserColor(memberId);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <UserAvatar
            name={displayName ?? ''}
            color={color}
            src={avatarUrl ?? undefined}
            size="sm"
          />
          <p className="truncate text-sm font-semibold">{displayName}</p>
        </div>
        {summary && summary.overdueTasksCount > 0 && (
          <Badge variant="destructive" className="shrink-0 gap-1 text-xs">
            <AlertCircle className="h-3 w-3" />
            {t('orgChart.memberNode.overdueBadge', { count: summary.overdueTasksCount })}
          </Badge>
        )}
      </div>

      {isLoading && (
        <div
          className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground"
          data-testid="org-chart-member-summary-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{t('orgChart.memberNode.stats.loading')}</span>
        </div>
      )}

      {summary && !isLoading && (
        <>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>
              {t('orgChart.memberNode.stats.total')}:{' '}
              <strong className="text-foreground">{summary.totalTasksCount}</strong>
            </span>
            <span>
              {t('orgChart.memberNode.stats.completed')}:{' '}
              <strong className="text-foreground">{summary.completedTasksCount}</strong>
            </span>
            <span>
              {t('orgChart.memberNode.stats.active')}:{' '}
              <strong className="text-foreground">{summary.activeTasksCount}</strong>
            </span>
            <span>
              {t('orgChart.memberNode.stats.efficiency')}:{' '}
              <strong className="text-foreground">{Math.round(summary.efficiencyPercent)}%</strong>
            </span>
          </div>

          {(summary.overdueTasks ?? []).length > 0 && (
            <>
              <Separator className="my-3" />
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="pb-1.5 text-left font-medium">
                      {t('orgChart.memberNode.table.name')}
                    </th>
                    <th className="pb-1.5 text-left font-medium">
                      {t('orgChart.memberNode.table.priority')}
                    </th>
                    <th className="pb-1.5 text-left font-medium">
                      {t('orgChart.memberNode.table.estimated')}
                    </th>
                    <th className="pb-1.5 text-left font-medium">
                      {t('orgChart.memberNode.table.tracked')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(summary.overdueTasks ?? []).map((task, i) => (
                    <tr key={task.id ?? i} className="border-b border-border/50 last:border-0">
                      <td className="py-1.5 pr-2">{task.title}</td>
                      <td className="py-1.5 pr-2">
                        <span className={cn('font-medium', priorityColor(task.priority))}>
                          {t(`orgChart.memberNode.priority.${priorityKey(task.priority)}`)}
                        </span>
                      </td>
                      <td className="py-1.5 pr-2 text-muted-foreground">
                        {task.estimatedHours != null ? `${task.estimatedHours}h` : '—'}
                      </td>
                      <td className="py-1.5 text-muted-foreground">{task.totalTrackedHours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}
